package com.oasisfeng.invaluable.actor

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.data.LinkTopology
import com.oasisfeng.invaluable.data.LinkView
import com.oasisfeng.invaluable.then
import kotlin.jvm.JvmInline

/**
 * Inter-Actor attention route: `subject -> recipient actor`.
 *
 * Any non-root subject routed to an actor is a route. Domain DSLs may read this as recommend,
 * follow, delivery, subscription, referral, etc., but those meanings do not change the topology.
 */
@JvmInline value class ActorRoute private constructor(override val link: NestedLink) : LinkView {

    companion object : LinkTopology<ActorRoute>(::ActorRoute) {
        /** Route [subject] to [recipient]'s attention. */
        operator fun invoke(subject: NestedLinkable, recipient: Actor) = ActorRoute(subject.link.then(recipient.link))
        /**
         * Any non-root Link whose target is an [Actor] is an actor route.
         *
         * This deliberately broad rule lets higher-layer DSLs read the same topology as delivery, follow,
         * subscription, invitation, or referral without minting protocol-specific anchors.
         */
        override fun NestedLink.matchesTopology() = parent().nestedOrNull() != null && Actor.matchNode(nestedTargetOrNull())
    }

    /** Actor whose attention is addressed by this route hop. */
    val recipient: Actor; get() = Actor.render(link.requireNestedTarget()) ?: error("Actor route target is not an actor.")

    /**
     * Canonical non-route subject addressed by this route chain.
     *
     * Relay hops are transparent: for `post -> bob -> carol`, both route hops have `post` as their subject.
     */
    val subject: NestedLink get() = link.ancestors().firstOrNull { render(it) == null } ?: error("Actor route chain has no subject.")

    /** Create the next route hop from this route. */
    fun relay(to: Actor): ActorRoute = ActorRoute(link.then(to.link))

    override fun toString() = link.toString()
}
